import React, { useState, useEffect } from "react";
import "../styles/AcademyPage.scss";
import { useMutation } from "@tanstack/react-query";
import { config } from "../config";
import { metaPixelEvents } from "../utils/metaPixel";
import { scrollToTop } from "../utils/scrollToTop";
import Carousel from "../Components/Carousel";
import doctorImg from "../images/aboutImg.png";
import academyLogo from "../images/academy-logo.png";
import academy1 from "../images/academy1.JPG";
import academy2 from "../images/academy2.JPG";
import academy3 from "../images/academy3.JPG";
import academy4 from "../images/academy4.jpeg";

const AcademyPage = () => {
  const BASE_URL = config.BackendUrl;
  const [customerName, setCustomerName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Mobile detection and scroll to top functionality
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async (emailData) => {
      const response = await fetch(`${BASE_URL}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailData),
      });
      if (!response.ok) {
        throw new Error("Failed to send email");
      }
      return response.json();
    },
    onSuccess: (data) => {
      setResponseMessage("המייל נשלח בהצלחה! ניצור איתך קשר בהקדם");
      setTimeout(() => setResponseMessage(""), 5000);

      // Track successful lead submission
      metaPixelEvents.trackLead("academy_form");

      resetForm();
    },
    onError: () => {
      setResponseMessage("שליחת המייל נכשלה. אנא נסה שוב.");
    },
  });

  // Validate form inputs
  const validateForm = () => {
    let newErrors = {};

    // Name validation
    if (!customerName.trim()) {
      newErrors.customerName = "שם מלא הוא שדה חובה";
    } else if (customerName.trim().length < 2) {
      newErrors.customerName = "השם חייב להכיל לפחות 2 תווים";
    } else if (
      !/^[א-ת\s\u0590-\u05FF\u200f\u200ea-zA-Z\s'-]+$/.test(customerName)
    ) {
      newErrors.customerName = "השם יכול להכיל רק אותיות בעברית או באנגלית";
    }

    // Phone validation - Israeli phone numbers
    if (!userPhone.trim()) {
      newErrors.userPhone = "מספר טלפון הוא שדה חובה";
    } else {
      // Remove all non-digit characters for validation
      const cleanPhone = userPhone.replace(/[^\d]/g, "");

      // Israeli phone number patterns
      const israeliMobilePattern = /^(05[0-9]|07[23789])\d{7}$/; // Mobile: 05X-XXXXXXX or 072/3/7/8/9-XXXXXXX
      const israeliLandlinePattern = /^0[2-4,8-9]\d{7}$/; // Landline: 02/03/04/08/09-XXXXXXX
      const internationalPattern = /^(\+972|972)[5-9]\d{8}$/; // International: +972 or 972

      if (cleanPhone.length < 9) {
        newErrors.userPhone = "מספר טלפון קצר מדי";
      } else if (cleanPhone.length > 13) {
        newErrors.userPhone = "מספר טלפון ארוך מדי";
      } else if (
        cleanPhone.startsWith("972") ||
        cleanPhone.startsWith("+972")
      ) {
        // International format
        if (!internationalPattern.test(cleanPhone.replace("+", ""))) {
          newErrors.userPhone =
            "מספר טלפון בינלאומי לא תקין (דוגמה: +972-50-1234567)";
        }
      } else if (cleanPhone.startsWith("0")) {
        // Israeli format
        if (
          !israeliMobilePattern.test(cleanPhone) &&
          !israeliLandlinePattern.test(cleanPhone)
        ) {
          newErrors.userPhone =
            "מספר טלפון ישראלי לא תקין (דוגמה: 050-1234567)";
        }
      } else {
        newErrors.userPhone = "מספר טלפון חייב להתחיל ב-0 או +972";
      }
    }

    // Email validation - comprehensive
    if (!userEmail.trim()) {
      newErrors.userEmail = "כתובת אימייל היא שדה חובה";
    } else {
      const emailPattern =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      const email = userEmail.trim().toLowerCase();

      if (!emailPattern.test(email)) {
        newErrors.userEmail = "כתובת אימייל לא תקינה (דוגמה: name@example.com)";
      } else if (email.length > 254) {
        newErrors.userEmail = "כתובת אימייל ארוכה מדי";
      } else if (email.split("@")[0].length > 64) {
        newErrors.userEmail = "החלק שלפני @ ארוך מדי";
      } else {
        // Check for common typos in domain
        const domain = email.split("@")[1];
        const suspiciousDomains = [
          "gmial.com",
          "gmai.com",
          "yahooo.com",
          "hotmial.com",
        ];

        if (suspiciousDomains.includes(domain)) {
          newErrors.userEmail = "יתכן שיש שגיאת הקלדה בכתובת האימייל";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Simple field change handlers without real-time validation
  const handleFieldChange = (fieldName, value) => {
    switch (fieldName) {
      case "customerName":
        setCustomerName(value);
        break;
      case "userPhone":
        setUserPhone(value);
        break;
      case "userEmail":
        setUserEmail(value);
        break;
      default:
        break;
    }

    // Clear errors when user starts typing after a failed submission
    if (showErrors && errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  };

  // Reset form
  const resetForm = () => {
    setCustomerName("");
    setUserEmail("");
    setUserPhone("");
    setErrors({});
    setShowErrors(false);
  };

  // Image gallery data
  const imageGallery = [
    {
      src: academy1,
      alt: "ד״ר שי תלם בהכשרה מעשית",
      title: "הכשרה מעשית",
      description: 'ד"ר תלם מדריך בתרגול אישי',
    },
    {
      src: academy2,
      alt: "סביבת הלמידה באקדמיה",
      title: "סביבת הלמידה",
      description: "מתקני האקדמיה המתקדמים",
    },
    {
      src: academy3,
      alt: "טכניקות מתקדמות באקדמיה",
      title: "טכניקות מתקדמות",
      description: "למידה של שיטות הזרקה מתקדמות",
    },
    {
      src: academy4,
      alt: "הכשרה מתקדמת באקדמיה",
      title: "הכשרה מתקדמת",
      description: 'ליווי צמוד ותרגול מעשי עם ד"ר תלם',
    },
  ];

  // Video gallery data
  const videoGallery = [
    {
      url: "https://youtube.com/embed/jmit_78MS40",
      thumbnail: "https://img.youtube.com/vi/jmit_78MS40/maxresdefault.jpg",
      title: "למה כדאי ללמוד באקדמיה שלנו?",
      description: 'גלו את היתרונות הייחודיים של האקדמיה של ד"ר תלם',
    },
    {
      url: "https://youtube.com/embed/z22NjNWR_Pg",
      thumbnail: "https://img.youtube.com/vi/z22NjNWR_Pg/maxresdefault.jpg",
      title: "הכשרה פרטית - מבט מקרוב",
      description: 'צפו בהכשרה אישית עם ד"ר תלם',
    },
    {
      url: "https://youtube.com/embed/t0q41BBjEE8",
      thumbnail: "https://img.youtube.com/vi/t0q41BBjEE8/maxresdefault.jpg",
      title: "חוויות בוגרים",
      description: "המלצות ועדויות מרופאים שסיימו את הקורס",
    },
    {
      url: "https://youtube.com/embed/AaFcOAdUOiU",
      thumbnail: "https://img.youtube.com/vi/AaFcOAdUOiU/maxresdefault.jpg",
      title: "טכניקות מתקדמות",
      description: "למידה מעשית של טכניקות הזרקה מתקדמות",
    },
    {
      url: "https://youtube.com/embed/IeokkFTg1RM",
      thumbnail: "https://img.youtube.com/vi/IeokkFTg1RM/maxresdefault.jpg",
      title: "למידה מעשית",
      description: 'תרגול מעשי עם ליווי אישי של ד"ר תלם',
    },
    {
      url: "https://youtube.com/embed/OplIK4JrmA8",
      thumbnail: "https://img.youtube.com/vi/OplIK4JrmA8/maxresdefault.jpg",
      title: "מאחורי הקלעים",
      description: "הצצה לתהליך ההכשרה באקדמיה",
    },
  ];

  // Scroll to contact form
  const scrollToContactForm = () => {
    const contactSection = document.querySelector(".contact-section");
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Video modal functions
  const openVideoModal = (videoItem, index) => {
    setSelectedVideo({
      url: videoItem.url,
      title: videoItem.title,
      description: videoItem.description,
    });
    setIsVideoModalOpen(true);
    document.body.classList.add("prevent-scroll"); // Prevent background scrolling
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setIsVideoModalOpen(false);
    document.body.classList.remove("prevent-scroll"); // Restore scrolling
  };

  // Image modal functions
  const openImageModal = (imageItem, index) => {
    setSelectedImageIndex(index);
    setIsImageModalOpen(true);
    document.body.classList.add("prevent-scroll");
  };

  const closeImageModal = () => {
    setSelectedImageIndex(null);
    setIsImageModalOpen(false);
    document.body.classList.remove("prevent-scroll");
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === imageGallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? imageGallery.length - 1 : prev - 1
    );
  };

  // Close modals on escape key
  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (isVideoModalOpen) {
          closeVideoModal();
        }
        if (isImageModalOpen) {
          closeImageModal();
        }
      }
    };

    const handleKeyNavigation = (event) => {
      if (isImageModalOpen) {
        if (event.key === "ArrowRight") {
          nextImage();
        } else if (event.key === "ArrowLeft") {
          prevImage();
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("keydown", handleKeyNavigation);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleKeyNavigation);
    };
  }, [isVideoModalOpen, isImageModalOpen, nextImage, prevImage]);

  // Handle form submission
  const onSubmit = (e) => {
    e.preventDefault();

    // Always show errors on submit attempt
    setShowErrors(true);

    if (validateForm()) {
      // Track form submission attempt
      metaPixelEvents.trackInitiateCheckout("academy_form");

      sendEmailMutation.mutate({
        customerName,
        emailMessage: `בקשה להצטרפות להכשרה פרטית 1 על 1 עם ד"ר תלם מ-${customerName}`,
        userEmail,
        userPhone,
      });
    }
  };
  return (
    <div className="academy-page">
      {/* Mobile Scroll to Top Area */}
      {isMobile && (
        <div
          className="mobile-scroll-top-area"
          onClick={() => scrollToTop()}
          style={{
            position: "fixed",
            top: "60px", // Start below header
            left: 0,
            right: 0,
            height: "40px", // Reduced height
            zIndex: 10010,
            cursor: "pointer",
            pointerEvents: "auto",
            background: "transparent",
          }}
          aria-label="Scroll to top"
        />
      )}

      {/* Hero Section */}
      <section className="academy-hero">
        <div className="hero-content">
          <div className="academy-logo">
            <img
              src={academyLogo}
              alt="ST Academy Logo"
              className="logo-image"
            />
          </div>
          <h1 className="academy-title">ד״ר שי תלם</h1>
          <h2 className="academy-subtitle">האקדמיה</h2>
        </div>
      </section>

      {/* Doctor Section */}
      <section className="doctor-section">
        <div className="container">
          <div className="doctor-content">
            <div className="doctor-image">
              <img src={doctorImg} alt="ד״ר שי תלם" />
              <div className="doctor-image-overlay">
                <h3 className="doctor-name">ד״ר שי תלם</h3>
                <p className="doctor-title">מייסד ומנהל האקדמיה</p>
              </div>
            </div>
            <div className="doctor-text">
              <h2 className="section-title">האקדמיה לרפואה אסתטית מתקדמת</h2>
              <p className="doctor-description">
                האקדמיה של ד״ר שי תלם נועדה לגשר על הפער שבין ידע תאורטי לבין
                עשייה קלינית אמיתית – ולהעניק לרופאים הכשרה שמכינה אותם לעבודה
                בשטח, לא רק למבחן.
              </p>
              <p className="doctor-description">
                הגישה מבוססת על ניסיון קליני רב־שנים, עבודה יומיומית עם מטופלים
                והתמודדות עם האתגרים האמיתיים של עולם האסתטיקה.
              </p>
              <p className="doctor-description desktop-only">
                לאורך ההכשרה תלמדו טכניקות מתקדמות בהזרקות, אבחון מבנה הפנים,
                תכנון טיפולים, עבודה עם טכנולוגיות חדשניות – והכול בליווי אישי.
              </p>
              <p className="doctor-description desktop-only">
                כל מפגש כולל תרגול מעשי על מטופלים אמיתיים, צפייה בטיפולים חיים
                וניתוח מעמיק של מקרים מהקליניקה.
              </p>
              <p className="doctor-description">
                הקורס בנוי כך שתצאו ממנו לא רק עם ידע – אלא עם ביטחון מקצועי,
                עבודה מדויקת ויכולת להשתלב בפרקטיקה האסתטית כבר מהיום הראשון.
              </p>
              <p className="doctor-description desktop-only">
                זוהי לא רק הכשרה, אלא הזדמנות לבנות בסיס חזק לקריירה שלך בתחום
                הצומח והמבוקש ביותר ברפואה כיום.
              </p>
            </div>
          </div>
        </div>

        {/* Scroll to Form Button */}
        <div className="section-cta-button">
          <button onClick={scrollToContactForm} className="scroll-to-form-btn">
            לקבלת סילבוס ופרטים נוספים
          </button>
        </div>
      </section>

      {/* Combined Features & Benefits Section */}
      <section className="features-benefits-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              אקדמיית הפרימיום להזרקות אסתטיות – ליווי אישי 1 על 1
            </h2>
            <p className="section-subtitle">
              אחרי שנים שבהן הכשרתי עשרות רופאים ורופאות באקדמיות המובילות
              בישראל ובמסגרת תכניות מקצועיות של אוניברסיטת תל־אביב, בחרתי ליצור
              פורמט ייחודי שמביא אתכם לרמה הגבוהה ביותר: הכשרה פרטית, מעשית
              ומדויקת – אחד על אחד.
            </p>
            <p className="section-description">
              כאן אין כיתה, אין לחץ, ואין רעש רקע. יש עומק, פרקטיקה, ביטחון
              ומיומנות אמיתית.
            </p>
          </div>

          {/* Premium Value Proposition */}
          <div className="premium-value-section">
            <div className="value-proposition-card">
              <div className="value-icon">
                <div className="premium-badge">
                  <div className="badge-inner">
                    <div className="minimal-icon icon-premium">★</div>
                  </div>
                </div>
              </div>
              <div className="value-content">
                <h3>דיוק, בטיחות ופרקטיקה שמייצרת תוצאות פרימיום</h3>
                <p>
                  הגישה שלי ברורה: ללמד טכניקה ברמה הגבוהה ביותר, אבל גם עיצוב,
                  סימטריה, אסתטיקה וחשיבה טיפולית מתקדמת.
                </p>
                <div className="value-highlights">
                  <span className="highlight-tag">ליווי אישי</span>
                  <span className="highlight-tag">תרגול מעשי</span>
                  <span className="highlight-tag">ללא הגבלות</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Features Grid */}
          <div className="features-showcase">
            <div className="features-grid-modern">
              <div className="feature-card-modern">
                <div className="feature-content-modern">
                  <h3>ליווי 1 על 1 בקצב שלך</h3>
                  <p>
                    כל מפגש מותאם אישית לצרכים שלך — לא של קבוצה. אתה קובע את
                    הקצב והעומק.
                  </p>
                  <div className="feature-benefit">
                    <span>התאמה אישית מלאה</span>
                  </div>
                </div>
              </div>

              <div className="feature-card-modern">
                <div className="feature-content-modern">
                  <h3>ללא הגבלת מזרקים</h3>
                  <p>
                    אתה מתרגל כמה מקרים שצריך על כל אינדיקציה, עם כמה שיותר
                    מזרקים במהלך כל מפגש.
                  </p>
                  <div className="feature-benefit">
                    <span>תרגול עד לשליטה מלאה</span>
                  </div>
                </div>
              </div>

              <div className="feature-card-modern">
                <div className="feature-content-modern">
                  <h3>המון תרגול מעשי אמיתי</h3>
                  <p>
                    לא תיאוריה בלבד — אתה עובד על מטופלים ומבצע את כל הפרוצדורות
                    בפועל.
                  </p>
                  <div className="feature-benefit">
                    <span>ניסיון קליני אמיתי</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll to Form Button */}
          <div className="section-cta-button">
            <button
              onClick={scrollToContactForm}
              className="scroll-to-form-btn"
            >
              לקבלת סילבוס ופרטים נוספים
            </button>
          </div>

          {/* Benefits & Social Proof Section */}
          <div className="benefits-social-section">
            <div className="benefits-showcase">
              <div className="benefits-header">
                <h4>הטבות בלעדיות לנרשמי הקורס</h4>
                <p>חבילה מקיפה שמבטיחה את ההצלחה שלך</p>
              </div>
              <div className="benefits-grid-modern">
                <div className="benefit-card-modern">
                  <div className="benefit-icon">
                    <div className="icon-video"></div>
                  </div>
                  <div className="benefit-text">
                    <h5>חבילת סרטוני פודקאסט שיווקיים</h5>
                    <p>
                      סדרת סרטונים מקצועיים שמשמשים כבסיס למיתוג האישי שלך ברשת.
                    </p>
                  </div>
                </div>
                <div className="benefit-card-modern">
                  <div className="benefit-icon">
                    <div className="icon-portfolio"></div>
                  </div>
                  <div className="benefit-text">
                    <h5>תיק עבודות אישי</h5>
                    <p>
                      תיק תמונות מקצועי מהפרקטיקה שלך בקורס — כלי חובה לכל רופא
                      שמקים פעילות אסתטית.
                    </p>
                  </div>
                </div>
                <div className="benefit-card-modern">
                  <div className="benefit-icon">
                    <div className="icon-support"></div>
                  </div>
                  <div className="benefit-text">
                    <h5>ליווי אישי לכל החיים</h5>
                    <p>
                      גם אחרי הקורס — אני אתך. שאלות מקצועיות, מקרים מורכבים,
                      בקרה וליווי מתמשך.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="social-proof-section">
              <div className="testimonials-modern">
                <div className="testimonials-header">
                  <h4>מה הרופאים שלנו אומרים</h4>
                  <div className="rating-display">
                    <div className="stars">★★★★★</div>
                  </div>
                </div>
                <div className="testimonials-carousel">
                  <div className="testimonial-card-modern">
                    <div className="quote-mark">"</div>
                    <blockquote>
                      למדתי המון, ואני מרגיש מוכן להתמודד עם כל מקרה. סוף־סוף
                      קיבלתי ביטחון אמיתי בידיים שלי.
                    </blockquote>
                    <div className="testimonial-author">
                      <strong>ד"ר שגיא </strong>
                    </div>
                  </div>
                  <div className="testimonial-card-modern">
                    <div className="quote-mark">"</div>
                    <blockquote>
                      הייתי כבר בקורס במקום אחר — הרמה פה אחרת לגמרי. זה הקורס
                      היחיד שבאמת נותן כלים פרקטיים ומדויקים.
                    </blockquote>
                    <div className="testimonial-author">
                      <strong>ד"ר אסתר </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll to Form Button */}
        <div className="section-cta-button">
          <button onClick={scrollToContactForm} className="scroll-to-form-btn">
            לקבלת סילבוס ופרטים נוספים
          </button>
        </div>
      </section>

      {/* Curriculum Section */}
      <section className="curriculum-section">
        <div className="container">
          <div className="curriculum-header">
            <h2 className="section-title">תכנית הקורס</h2>
            <div className="course-stats">
              <div className="stat-item">
                <span className="stat-number">5</span>
                <span className="stat-label">מפגשים</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">25</span>
                <span className="stat-label">שעות</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">1:1</span>
                <span className="stat-label">ליווי אישי</span>
              </div>
            </div>
          </div>

          <div className="curriculum-grid-layout">
            <div className="curriculum-row">
              <div className="lesson-module">
                <div className="module-header">
                  <div className="module-number">1</div>
                  <h4>אנטומיה ובוטוקס</h4>
                  <p className="module-subtitle">שלושת האזורים הקלאסיים</p>
                </div>
                <div className="module-content">
                  <ul className="topic-list">
                    <li>מבנה העור: שכבות, שומן, כלי דם ושרירים</li>
                    <li>אנטומיה פונקציונלית של שרירי ההבעה</li>
                    <li>מנגנון הפעולה של בוטולינום טוקסין</li>
                    <li>טכניקות הזרקה: גלבלה, מצח, רגלי עורב</li>
                    <li>בטיחות, מינונים ותכנון אישי</li>
                  </ul>
                  <div className="practice-note">הדגמה + תרגול מעשי</div>
                </div>
              </div>

              <div className="lesson-module">
                <div className="module-header">
                  <div className="module-number">2</div>
                  <h4>פיסול השליש העליון והאמצעי</h4>
                  <p className="module-subtitle">רקות, לחיים ונאזולאביאלים</p>
                </div>
                <div className="module-content">
                  <ul className="topic-list">
                    <li>אנטומיה של הרקות, לחיים ונאזולאביאלים</li>
                    <li>מילוי רקות</li>
                    <li>פיסול עצמות לחיים</li>
                    <li>קנולה מול מחט — יתרונות וחסרונות</li>
                  </ul>
                  <div className="practice-note">הדגמה + תרגול מעשי</div>
                </div>
              </div>
            </div>

            <div className="curriculum-row">
              <div className="lesson-module">
                <div className="module-header">
                  <div className="module-number">3</div>
                  <h4>פיסול השליש התחתון</h4>
                  <p className="module-subtitle">לסת, סנטר ובוטוקס צוואר</p>
                </div>
                <div className="module-content">
                  <ul className="topic-list">
                    <li>אנטומיה של קו הלסת, סנטר ומריונטות</li>
                    <li>חידוד קו הלסת</li>
                    <li>הדגשת סנטר</li>
                    <li>שילוב בוטוקס לשליש התחתון ולצוואר</li>
                  </ul>
                  <div className="practice-note">הדגמה + תרגול מעשי</div>
                </div>
              </div>

              <div className="lesson-module">
                <div className="module-header">
                  <div className="module-number">4</div>
                  <h4>שפתיים ואף</h4>
                  <p className="module-subtitle">עיצוב וניהול סיבוכים</p>
                </div>
                <div className="module-content">
                  <ul className="topic-list">
                    <li>אנטומיה של השפתיים</li>
                    <li>טכניקות לעיצוב טבעי ובטוח</li>
                    <li>פיסול אף — עקרונות ושיטות</li>
                    <li>ניהול סיבוכים: חסימה, בצקות, א-סימטריה ועוד</li>
                  </ul>
                  <div className="practice-note">הדגמה + תרגול מעשי</div>
                </div>
              </div>
            </div>

            <div className="curriculum-row final-row">
              <div className="lesson-module final-module">
                <div className="module-header">
                  <div className="module-number">5</div>
                  <h4>שקעי עיניים ואיכות העור</h4>
                  <p className="module-subtitle">ביוסטימולטורים ומזותרפיה</p>
                </div>
                <div className="module-content">
                  <ul className="topic-list">
                    <li>מתי כן ומתי לא להזריק בשקעי עיניים</li>
                    <li>ביוסטימולטורים</li>
                    <li>סקין בוסטרים</li>
                    <li>מזותרפיה פנים/צוואר – חומרים ושיטות</li>
                  </ul>
                  <div className="practice-note graduation">
                    חלוקת תעודות סיום
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll to Form Button */}
        <div className="section-cta-button">
          <button onClick={scrollToContactForm} className="scroll-to-form-btn">
            לקבלת סילבוס ופרטים נוספים
          </button>
        </div>
      </section>

      {/* Academy Address Section */}
      <section className="academy-address-section">
        <div className="container">
          <div className="academy-address-container">
            <div className="address-icon-wrapper">
              <div className="address-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="currentColor"
                  />
                </svg>
              </div>
            </div>

            <div className="address-content-redesigned">
              <h3 className="address-title">מיקום האקדמיה</h3>

              <div className="address-single-card">
                <div className="address-section main-section">
                  <div className="section-content">
                    <div className="address-text">מגדלי בסר 2</div>
                    <div className="address-details">רמת גן • קומה 7</div>
                    <div className="address-comment">
                      כתובת מדויקת: רח' בן גוריון 1 בני ברק
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Media Gallery Section */}
      <section className="media-gallery-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">גלריית תמונות וסרטונים</h2>
            <p className="section-subtitle">
              הכירו את ד"ר תלם והאקדמיה דרך תמונות וסרטונים מהפעילות המקצועית
            </p>
          </div>

          <div className="media-carousels">
            {/* Videos Carousel */}
            <div className="media-category">
              <h3 className="category-title">סרטונים מהאקדמיה</h3>
              <div className="carousel-wrapper">
                {videoGallery && videoGallery.length > 0 ? (
                  <Carousel
                    items={videoGallery}
                    type="video"
                    autoPlay={false}
                    autoPlayInterval={6000}
                    showDots={false}
                    showArrows={true}
                    showCounter={false}
                    itemsPerView={isMobile ? 1 : 2}
                    onItemClick={openVideoModal}
                    className="videos-carousel"
                  />
                ) : (
                  <div
                    style={{
                      height: "280px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "12px",
                      color: "white",
                    }}
                  >
                    אין סרטונים זמינים כרגע
                  </div>
                )}
              </div>
            </div>

            {/* Images Carousel */}
            <div className="media-category">
              <h3 className="category-title">תמונות מהאקדמיה</h3>
              <div className="carousel-wrapper">
                {imageGallery && imageGallery.length > 0 ? (
                  <Carousel
                    items={imageGallery}
                    type="image"
                    autoPlay={false}
                    showDots={false}
                    showArrows={true}
                    showCounter={false}
                    itemsPerView={isMobile ? 1 : 2}
                    onItemClick={openImageModal}
                    className="images-carousel"
                  />
                ) : (
                  <div
                    style={{
                      height: "280px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "12px",
                      color: "white",
                    }}
                  >
                    אין תמונות זמינות כרגע
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Scroll to Form Button */}
          <div className="section-cta-button">
            <button
              onClick={scrollToContactForm}
              className="scroll-to-form-btn"
            >
              לקבלת סילבוס ופרטים נוספים
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <h2 className="section-title">
            מעוניין להצטרף להכשרה פרטית 1 על 1 עם ד"ר תלם?
          </h2>
          <p className="contact-description">
            השאר פרטים לשיחה אישית ואבדוק את התאמתך למחזור הקרוב.
          </p>

          <div className="contact-form">
            <form onSubmit={onSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="השם שלך"
                  value={customerName}
                  onChange={(e) =>
                    handleFieldChange("customerName", e.target.value)
                  }
                  className={showErrors && errors.customerName ? "error" : ""}
                  required
                />
                {showErrors && errors.customerName && (
                  <div className="error-message">{errors.customerName}</div>
                )}
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  placeholder="טלפון"
                  value={userPhone}
                  onChange={(e) =>
                    handleFieldChange("userPhone", e.target.value)
                  }
                  className={showErrors && errors.userPhone ? "error" : ""}
                  required
                />
                {showErrors && errors.userPhone && (
                  <div className="error-message">{errors.userPhone}</div>
                )}
              </div>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="אימייל"
                  value={userEmail}
                  onChange={(e) =>
                    handleFieldChange("userEmail", e.target.value)
                  }
                  className={showErrors && errors.userEmail ? "error" : ""}
                  required
                />
                {showErrors && errors.userEmail && (
                  <div className="error-message">{errors.userEmail}</div>
                )}
              </div>
              <button
                type="submit"
                className="submit-btn"
                disabled={sendEmailMutation.isPending}
              >
                {sendEmailMutation.isPending ? "שולח..." : "שלח פנייה"}
              </button>
              {responseMessage && (
                <div
                  className={`response-message ${
                    responseMessage.includes("בהצלחה") ? "success" : "error"
                  }`}
                >
                  {responseMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {isVideoModalOpen && selectedVideo && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div
            className="video-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="video-modal-close" onClick={closeVideoModal}>
              ✕
            </button>
            <div className="video-modal-header">
              <h3>{selectedVideo.title}</h3>
              <p>{selectedVideo.description}</p>
            </div>
            <div className="video-modal-player">
              <iframe
                src={selectedVideo.url}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {isImageModalOpen && selectedImageIndex !== null && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="image-modal-close" onClick={closeImageModal}>
              ✕
            </button>

            {/* Navigation Arrows */}
            <button className="image-nav-btn prev-btn" onClick={prevImage}>
              ◀
            </button>
            <button className="image-nav-btn next-btn" onClick={nextImage}>
              ▶
            </button>

            {/* Image Display */}
            <div className="image-modal-display">
              <img
                src={imageGallery[selectedImageIndex].src}
                alt={imageGallery[selectedImageIndex].alt}
                className="modal-image"
              />
            </div>

            {/* Image Info */}
            <div className="image-modal-info">
              <h3>{imageGallery[selectedImageIndex].title}</h3>
              <p>{imageGallery[selectedImageIndex].description}</p>
              <div className="image-counter">
                {selectedImageIndex + 1} / {imageGallery.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyPage;
